import type { GameEvent } from '../useGameStore';
import { TEAM_FAULT_ACTIONS } from '../../domain-ui/action/teamFaults';

export const PHANTOM_ACTIONS: ReadonlySet<string> = new Set([
  'Temps Mort',
  'Temps Mort Adversaire',
  'Substitution',
  'SET_START',
  'LIBERO_CHANGE',
  'SKIP_TO_SCORE',
  'MATCH_END',
]);

const POINT_ENDING_FAULT_ACTIONS: ReadonlySet<string> = new Set([
  'Point Adversaire',
  ...TEAM_FAULT_ACTIONS,
]);

/** Fautes d'équipe : le point va à l'équipe ADVERSE de leur auteur. */
const TEAM_FAULTS: ReadonlySet<string> = new Set(TEAM_FAULT_ACTIONS);

/**
 * Équipe auteur d'une action, vue depuis notre équipe : `teamSide` quand il est
 * posé (deux équipes), sinon `isOpponent` (une équipe, adverse ou non).
 */
export function eventAuthorSide(ev: GameEvent): 'us' | 'them' {
  if (ev.teamSide === 'away') return 'them';
  if (ev.teamSide === 'home') return 'us';
  return ev.isOpponent ? 'them' : 'us';
}

const HASH_TERMINAL_ACTIONS: ReadonlySet<string> = new Set([
  'Service',
  'Attaque',
  'Bloc',
  'Retour Direct',
  'Faute Adversaire',
]);

// Attack / direct return "/" = stuffed by the block; block "/" = invasion (DataVolley
// convention, VOL-189). Both cost the point to their author.
export const SLASH_TERMINAL_ACTIONS: ReadonlySet<string> = new Set(['Attaque', 'Retour Direct', 'Bloc']);

/**
 * A fault charged to its author: "=" on any action, plus the block invasion "/".
 * An attack "/" is not one: the stats count it apart, as "stuffed".
 */
export function isFaultAction(action: string, quality: string): boolean {
  return quality === '=' || (action === 'Bloc' && quality === '/');
}

export function isPhantomEvent(ev: GameEvent): boolean {
  return PHANTOM_ACTIONS.has(ev.action as string);
}

export function isTerminalEvent(ev: GameEvent): boolean {
  return isTerminalAction(ev.action as string, ev.quality as string);
}

/**
 * Une action de ce type et de cette qualité termine-t-elle le point ? Même règle
 * que le serveur (`isTerminalEvent` du service d'écriture), pour les écrans qui
 * manipulent des actions hors du store live (historique, revue).
 */
export function isTerminalAction(action: string, quality: string): boolean {
  return (
    quality === '=' ||
    (quality === '#' && HASH_TERMINAL_ACTIONS.has(action)) ||
    (quality === '/' && SLASH_TERMINAL_ACTIONS.has(action)) ||
    POINT_ENDING_FAULT_ACTIONS.has(action)
  );
}

/**
 * Deux événements appartiennent-ils au MÊME rallye ? Le score porté par un événement
 * est celui d'AVANT l'action : il est donc identique pour toutes les lignes d'un
 * échange, et différent du rallye suivant (un point est marqué entre les deux).
 *
 * Utile depuis qu'un échange peut se clôturer sur plusieurs lignes terminales —
 * une attaque contrée (`/`) puis le bloc qui la conclut (`#`) décrivent la même fin
 * de rallye et ne valent qu'un point.
 */
export function isSameRally(a: GameEvent, b: GameEvent): boolean {
  return a.set === b.set && a.scoreUs === b.scoreUs && a.scoreThem === b.scoreThem;
}

/**
 * Prochain événement réel (hors phantom) après `index`, ou `null`.
 */
export function nextRealEvent(events: GameEvent[], index: number): GameEvent | null {
  for (let i = index + 1; i < events.length; i++) {
    if (!isPhantomEvent(events[i])) return events[i];
  }
  return null;
}

export function computePointWinnerFromEvent(ev: GameEvent): 'us' | 'them' {
  const action = ev.action as string;
  const isOpp = !!ev.isOpponent;
  // Une faute d'équipe coûte le point à SON auteur. Elle revenait toujours à
  // l'adversaire, même quand c'était lui qui l'avait commise — en mode
  // « équipe adverse » et en deux équipes, « Modifier le dernier point »
  // retirait alors le point au mauvais camp. Même règle que le rejeu serveur.
  if (TEAM_FAULTS.has(action)) return eventAuthorSide(ev) === 'us' ? 'them' : 'us';
  if (POINT_ENDING_FAULT_ACTIONS.has(action)) return 'them';
  if (action === 'Faute Adversaire') return 'us';
  if (ev.quality === '#') return isOpp ? 'them' : 'us';
  return isOpp ? 'us' : 'them';
}
